import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Modal, Popconfirm, Spin, Table, Tag, Typography } from "antd";
import moment from "moment";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import { APIS } from "../../../app/utils/constants/apiConstant";
import {
  checkPermission,
  hasAdminAccess,
  permissionType,
  showErrorResponse,
} from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import {
  fetchSopDocumentBlob,
  isImageMimeType,
  isPdfMimeType,
} from "../../../app/utils/common/sopDocumentFile";
import PageContainerHeader from "../../molecules/PageContainerHeader/PageContainerHeader";
import EmployeeSelect from "../../atoms/EmployeeSelect/EmployeeSelect";
import { useSelector } from "react-redux";

const { Text, Title } = Typography;

const SopsPage = () => {
  const { user } = useSelector((state) => state.auth.auth);
  const [loading, setLoading] = useState(true);
  const [sops, setSops] = useState([]);
  const [canManageAll, setCanManageAll] = useState(false);
  const [canAssign, setCanAssign] = useState(false);

  const [title, setTitle] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [viewingSop, setViewingSop] = useState(null);
  const [viewUrl, setViewUrl] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningSop, setAssigningSop] = useState(null);
  const [assignUserIds, setAssignUserIds] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);

  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  const canRead =
    hasAdminAccess(user) || checkPermission(permissionType.SOP_MANAGEMENT, "read");
  const canCreate =
    hasAdminAccess(user) || checkPermission(permissionType.SOP_MANAGEMENT, "create");
  const canDelete =
    hasAdminAccess(user) || checkPermission(permissionType.SOP_MANAGEMENT, "delete");
  const canAssignEmployees =
    hasAdminAccess(user) || checkPermission(permissionType.SOP_MANAGEMENT, "assign employee");

  const loadSops = useCallback(async () => {
    setLoading(true);
    try {
      const response = await protectedApi({
        url: APIS.SOPS,
        method: "GET",
      });
      const data = response?.data?.data;
      setSops(Array.isArray(data?.sops) ? data.sops : []);
      setCanManageAll(Boolean(data?.canManageAll));
      setCanAssign(Boolean(data?.canAssign));
    } catch (error) {
      showErrorResponse(error);
      setSops([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canRead) loadSops();
  }, [canRead, loadSops]);

  useEffect(() => {
    return () => {
      setViewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  const handleFilePick = (event) => {
    setUploadFile(event.target.files?.[0] || null);
  };

  const handleUpload = async () => {
    if (!canCreate) return;
    const safeTitle = title.trim();
    if (!safeTitle) {
      showErrorToast("Enter an SOP title.");
      return;
    }
    if (!uploadFile) {
      showErrorToast("Choose a file to upload.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("title", safeTitle);
      if (canAssignEmployees && assignedUserIds.length) {
        formData.append("assignedUserIds", JSON.stringify(assignedUserIds));
      }
      await protectedApi({
        url: APIS.SOPS,
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        _dataEncrypted: true,
      });
      showSuccessToast("SOP uploaded.");
      setTitle("");
      setUploadFile(null);
      setAssignedUserIds([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadSops();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (sop) => {
    if (!sop?.id) return;
    setViewingSop(sop);
    setViewLoading(true);
    setViewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const blob = await fetchSopDocumentBlob(sop.id);
      const mimeType = sop.mimeType || blob.type;
      if (isPdfMimeType(mimeType) || isImageMimeType(mimeType)) {
        setViewUrl(URL.createObjectURL(blob));
      } else {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        setViewingSop(null);
      }
    } catch (error) {
      showErrorToast(error?.message || "Unable to open SOP.");
      setViewingSop(null);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewingSop(null);
    setViewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const openAssignModal = async (sop) => {
    if (!canAssignEmployees || !sop?.id) return;
    setAssigningSop(sop);
    setAssignModalOpen(true);
    setAssignLoading(true);
    setAssignUserIds([]);
    try {
      const response = await protectedApi({
        url: APIS.SOP_BY_ID.replace(":sopId", String(sop.id)),
        method: "GET",
      });
      const assigned = response?.data?.data?.assignedUsers;
      setAssignUserIds(
        Array.isArray(assigned) ? assigned.map((u) => u.userId) : []
      );
    } catch (error) {
      showErrorResponse(error);
      setAssignModalOpen(false);
      setAssigningSop(null);
    } finally {
      setAssignLoading(false);
    }
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setAssigningSop(null);
    setAssignUserIds([]);
  };

  const saveAssignments = async () => {
    if (!assigningSop?.id) return;
    setAssignSaving(true);
    try {
      await protectedApi({
        url: APIS.SOP_ASSIGNMENTS.replace(":sopId", String(assigningSop.id)),
        method: "PUT",
        data: { userIds: assignUserIds },
      });
      showSuccessToast("SOP assignments updated.");
      closeAssignModal();
      await loadSops();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setAssignSaving(false);
    }
  };

  const handleDelete = async (sop) => {
    if (!canDelete || !sop?.id) return;
    setDeleteLoadingId(sop.id);
    try {
      await protectedApi({
        url: APIS.SOP_BY_ID.replace(":sopId", String(sop.id)),
        method: "DELETE",
      });
      showSuccessToast("SOP removed.");
      if (viewingSop?.id === sop.id) closeViewModal();
      await loadSops();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (value) => <span className="font-medium text-[#1f2b44]">{value}</span>,
    },
    {
      title: "File",
      dataIndex: "fileName",
      key: "fileName",
      render: (value) => value || "—",
    },
    ...(canManageAll
      ? [
          {
            title: "Assigned",
            dataIndex: "assignedCount",
            key: "assignedCount",
            width: 120,
            render: (count) =>
              count > 0 ? (
                <Tag color="blue">
                  {count} employee{count === 1 ? "" : "s"}
                </Tag>
              ) : (
                <Tag>Unassigned</Tag>
              ),
          },
        ]
      : []),
    {
      title: "Uploaded",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (value) => (value ? moment(value).format("D MMM YYYY, h:mm A") : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 280,
      render: (_, row) => (
        <div className="flex flex-wrap gap-2">
          <Button size="small" onClick={() => handleView(row)}>
            View
          </Button>
          {canAssignEmployees ? (
            <Button size="small" onClick={() => openAssignModal(row)}>
              Assign
            </Button>
          ) : null}
          {canDelete ? (
            <Popconfirm
              title="Delete this SOP?"
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(row)}
            >
              <Button size="small" danger loading={deleteLoadingId === row.id}>
                Delete
              </Button>
            </Popconfirm>
          ) : null}
        </div>
      ),
    },
  ];

  if (!canRead) {
    return (
      <div className="p-6">
        <Text type="danger">You do not have permission to view SOPs.</Text>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col gap-6">
      <PageContainerHeader title="Standard Operating Procedures (SOPs)" />

      <p className="text-sm text-gray-600 -mt-2">
        {canManageAll
          ? "Upload SOPs and assign them to employees. Employees only see SOPs assigned to them."
          : "SOPs assigned to you appear below."}
      </p>

      {canCreate ? (
        <div className="border border-solid border-[#e7ebf6] rounded-[14px] p-6 bg-white shadow-sm flex flex-col gap-4 max-w-3xl">
          <Title level={5} className="!mb-0">
            Upload SOP
          </Title>
          <div>
            <label className="block text-sm font-semibold text-[#202b44] mb-2">Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Onboarding checklist"
              maxLength={500}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#202b44] mb-2">File</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
              onChange={handleFilePick}
            />
            {uploadFile ? (
              <Text type="secondary" className="block mt-1 text-sm">
                Selected: {uploadFile.name}
              </Text>
            ) : null}
          </div>
          {canAssignEmployees ? (
            <div>
              <label className="block text-sm font-semibold text-[#202b44] mb-2">
                Assign to employees (optional)
              </label>
              <EmployeeSelect
                isMultiple
                value={assignedUserIds}
                onSelectEmployee={setAssignedUserIds}
                width="100%"
                placeholder="Select employees..."
                excludeCeoCmo={false}
              />
            </div>
          ) : null}
          <div>
            <Button type="primary" loading={uploading} onClick={handleUpload}>
              Upload SOP
            </Button>
          </div>
        </div>
      ) : null}

      <div className="border border-solid border-[#e7ebf6] rounded-[14px] p-6 bg-white shadow-sm">
        <Title level={5} className="!mb-4">
          {canManageAll ? "All SOPs" : "My SOPs"}
        </Title>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={sops}
          columns={columns}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{
            emptyText: canManageAll
              ? "No SOPs uploaded yet."
              : "No SOPs have been assigned to you yet.",
          }}
          scroll={{ x: 720 }}
        />
      </div>

      <Modal
        title={viewingSop?.title || "View SOP"}
        open={Boolean(viewingSop)}
        onCancel={closeViewModal}
        footer={null}
        width="90vw"
        style={{ maxWidth: 960 }}
        destroyOnClose
      >
        {viewLoading ? (
          <div className="flex justify-center py-12">
            <Spin />
          </div>
        ) : viewUrl ? (
          isPdfMimeType(viewingSop?.mimeType) ? (
            <iframe
              title={viewingSop?.title || "SOP"}
              src={viewUrl}
              className="w-full border-0"
              style={{ height: "75vh" }}
            />
          ) : (
            <img src={viewUrl} alt={viewingSop?.title || "SOP"} className="max-w-full mx-auto" />
          )
        ) : null}
      </Modal>

      <Modal
        title={assigningSop ? `Assign: ${assigningSop.title}` : "Assign SOP"}
        open={assignModalOpen}
        onCancel={closeAssignModal}
        onOk={saveAssignments}
        okText="Save assignments"
        confirmLoading={assignSaving}
        destroyOnClose
      >
        {assignLoading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : (
          <div className="py-2">
            <label className="block text-sm font-semibold text-[#202b44] mb-2">
              Employees
            </label>
            <EmployeeSelect
              isMultiple
              value={assignUserIds}
              onSelectEmployee={setAssignUserIds}
              width="100%"
              placeholder="Select employees to assign this SOP..."
              excludeCeoCmo={false}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SopsPage;
